import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, BarChart3, Lock } from "lucide-react";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { LEGAL } from "@/constants/testIds";

export default function CookiePreferencesModal() {
  const { prefsOpen, closePreferences, consent, savePreferences, acceptAll, rejectAll } = useCookieConsent();
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (prefsOpen) setAnalytics(!!consent?.analytics);
  }, [prefsOpen, consent]);

  return (
    <Dialog open={prefsOpen} onOpenChange={(o) => (!o ? closePreferences() : null)}>
      <DialogContent
        data-testid={LEGAL.cookiePrefsModal}
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Configuración de cookies</DialogTitle>
          <DialogDescription>
            Gestiona tus preferencias. Las cookies necesarias no pueden desactivarse. Consulta la{" "}
            <Link to="/politica-de-cookies" onClick={closePreferences} className="font-semibold text-primary underline underline-offset-2">
              Política de Cookies
            </Link>{" "}
            para más detalle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Necesarias */}
          <div className="rounded-xl border border-border bg-secondary/40 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-foreground">Cookies necesarias</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Imprescindibles para el funcionamiento básico de la web y para recordar tus preferencias de cookies.
                    Están siempre activas.
                  </p>
                </div>
              </div>
              <span className="mt-1 shrink-0 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                Siempre activas
              </span>
            </div>
          </div>

          {/* Analíticas */}
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <BarChart3 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold text-foreground">Cookies analíticas</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Nos permiten medir y analizar de forma anónima el uso de la web para mejorar la experiencia.
                    Desactivadas por defecto.
                  </p>
                </div>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                data-testid={LEGAL.cookieSwitchAnalytics}
                aria-label="Activar cookies analíticas"
              />
            </div>
          </div>

          {/* Marketing: la web no utiliza cookies publicitarias */}
          <div className="rounded-xl border border-dashed border-border p-4 opacity-80">
            <div className="flex gap-3">
              <Lock className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div>
                <p className="font-semibold text-foreground">Cookies de marketing o publicitarias</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Esta web <span className="font-semibold">no utiliza</span> cookies de marketing, publicidad ni
                  seguimiento comercial. Por ello no hay ninguna opción que configurar.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2.5 sm:flex-row sm:justify-end">
          <button
            onClick={rejectAll}
            data-testid={LEGAL.cookiePrefsRejectAll}
            className="rounded-full border border-border bg-white px-5 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-secondary"
          >
            Rechazar opcionales
          </button>
          <button
            onClick={acceptAll}
            data-testid={LEGAL.cookiePrefsAcceptAll}
            className="rounded-full border border-primary/30 bg-accent px-5 py-2.5 text-sm font-bold text-primary transition-colors hover:bg-primary/10"
          >
            Aceptar todas
          </button>
          <button
            onClick={() => savePreferences({ analytics })}
            data-testid={LEGAL.cookieSavePrefs}
            className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-transform hover:-translate-y-0.5"
          >
            Guardar configuración
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
